import { useState, useEffect } from "react";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Shield, Smartphone, Key, Copy, CheckCircle2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import QRCode from "qrcode";

export function TwoFactorAuthSettings() {
  const { t } = useTranslation();
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  
  // Enable 2FA flow
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  
  // Disable 2FA flow
  const [disablePassword, setDisablePassword] = useState("");
  
  // Backup codes count
  const [backupCodesCount, setBackupCodesCount] = useState(0);

  // Queries and mutations
  const { data: statusData, refetch: refetchStatus } = trpc.auth.twoFactor.status.useQuery();
  const { data: backupCodesData, refetch: refetchBackupCodes } = trpc.auth.twoFactor.getBackupCodesCount.useQuery();
  
  const generateSecretMutation = trpc.auth.twoFactor.generateSecret.useMutation({
    onSuccess: async (data) => {
      setSecret(data.secret);
      // Generate QR code as data URL
      try {
        const qrDataUrl = await QRCode.toDataURL(data.otpauthUrl);
        setQrCodeUrl(qrDataUrl);
      } catch (err) {
        toast.error("Fehler beim Generieren des QR-Codes");
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const enableMutation = trpc.auth.twoFactor.enable.useMutation({
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setShowEnableDialog(false);
      setShowBackupCodesDialog(true);
      setIs2FAEnabled(true);
      refetchStatus();
      refetchBackupCodes();
      toast.success("Zwei-Faktor-Authentifizierung aktiviert!");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const disableMutation = trpc.auth.twoFactor.disable.useMutation({
    onSuccess: () => {
      setShowDisableDialog(false);
      setDisablePassword("");
      setIs2FAEnabled(false);
      refetchStatus();
      toast.success("Zwei-Faktor-Authentifizierung deaktiviert");
    },
    onError: (error) => {
      toast.error(error.message);
      setDisablePassword("");
    },
  });

  const regenerateBackupCodesMutation = trpc.auth.twoFactor.regenerateBackupCodes.useMutation({
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setShowBackupCodesDialog(true);
      refetchBackupCodes();
      toast.success("Neue Backup-Codes generiert");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    if (statusData) {
      setIs2FAEnabled(statusData.enabled);
    }
  }, [statusData]);

  useEffect(() => {
    if (backupCodesData) {
      setBackupCodesCount(backupCodesData.count);
    }
  }, [backupCodesData]);

  const handleEnableClick = () => {
    setShowEnableDialog(true);
    generateSecretMutation.mutate();
  };

  const handleVerifyAndEnable = () => {
    if (verificationCode.length !== 6) {
      toast.error("Bitte geben Sie einen 6-stelligen Code ein");
      return;
    }
    enableMutation.mutate({
      secret,
      token: verificationCode,
    });
  };

  const handleDisable = () => {
    if (!disablePassword) {
      toast.error("Bitte geben Sie Ihr Passwort ein");
      return;
    }
    disableMutation.mutate({
      password: disablePassword,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("In Zwischenablage kopiert");
  };

  const copyAllBackupCodes = () => {
    const allCodes = backupCodes.join("\n");
    navigator.clipboard.writeText(allCodes);
    toast.success("Alle Backup-Codes kopiert");
  };

  return (
    <>
      <Card className="backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 border-purple-200 dark:border-purple-800 shadow-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t.settings.twoFactorAuth}
          </CardTitle>
          <CardDescription>
            {t.settings.twoFactorAuthDescription}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* 2FA Status */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {is2FAEnabled ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              )}
              <div>
                <p className="font-medium">
                  {is2FAEnabled ? t.settings.twoFactorEnabled : t.settings.twoFactorDisabled}
                </p>
                <p className="text-sm text-muted-foreground">
                  {is2FAEnabled
                    ? t.settings.twoFactorProtected
                    : t.settings.twoFactorNotProtected}
                </p>
              </div>
            </div>
            <Switch
              checked={is2FAEnabled}
              onCheckedChange={(checked) => {
                if (checked) {
                  handleEnableClick();
                } else {
                  setShowDisableDialog(true);
                }
              }}
            />
          </div>

          {/* Backup Codes Info */}
          {is2FAEnabled && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <Key className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-medium">{t.settings.twoFactorBackupCodes}</p>
                    <p className="text-sm text-muted-foreground">
                      {t.settings.twoFactorBackupCodesRemaining.replace('{count}', String(backupCodesCount))}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => regenerateBackupCodesMutation.mutate()}
                  disabled={regenerateBackupCodesMutation.isPending}
                >
                  {t.settings.twoFactorGenerateNew}
                </Button>
              </div>

              <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                <div className="flex gap-2 mb-2">
                  <Smartphone className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      {t.settings.twoFactorImportantNote}
                    </p>
                    <p className="text-blue-700 dark:text-blue-300 mt-1">
                      {t.settings.twoFactorAuthenticatorNote}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enable 2FA Dialog */}
      <Dialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.settings.twoFactorEnableTitle}</DialogTitle>
            <DialogDescription>
              {t.settings.twoFactorEnableDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {qrCodeUrl && (
              <div className="flex flex-col items-center gap-4">
                <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t.settings.twoFactorManualCode}
                  </p>
                  <div className="flex items-center gap-2 justify-center">
                    <code className="px-3 py-1 bg-muted rounded text-sm font-mono">
                      {secret}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(secret)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="verification-code">
                {t.settings.twoFactorVerificationCode}
              </Label>
              <Input
                id="verification-code"
                type="text"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                placeholder={t.settings.twoFactorVerificationCodePlaceholder}
                className="text-center text-2xl tracking-widest"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEnableDialog(false);
                setVerificationCode("");
                setQrCodeUrl("");
                setSecret("");
              }}
            >
              {t.common.cancel}
            </Button>
            <Button
              onClick={handleVerifyAndEnable}
              disabled={enableMutation.isPending || verificationCode.length !== 6}
            >
              {t.settings.twoFactorEnableTitle.split(' ')[0]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.settings.twoFactorDisableTitle}</DialogTitle>
            <DialogDescription>
              {t.settings.twoFactorDisableDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable-password">{t.settings.password}</Label>
              <Input
                id="disable-password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="●●●●●●●●"
              />
            </div>
            <div className="p-3 border border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {t.settings.twoFactorDisableWarning}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDisableDialog(false);
                setDisablePassword("");
              }}
            >
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable}
              disabled={disableMutation.isPending}
            >
              {t.settings.twoFactorDisableTitle.split(' ')[0]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodesDialog} onOpenChange={setShowBackupCodesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.settings.twoFactorBackupCodesTitle}</DialogTitle>
            <DialogDescription>
              {t.settings.twoFactorBackupCodesDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-muted rounded font-mono text-sm"
                >
                  <span>{code}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(code)}
                    className="h-6 w-6 p-0"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={copyAllBackupCodes}
            >
              <Copy className="h-4 w-4 mr-2" />
              {t.settings.twoFactorBackupCodesCopyAll}
            </Button>
            <div className="p-3 border border-red-200 bg-red-50 dark:bg-red-950/20 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">
                {t.settings.twoFactorBackupCodesImportant}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowBackupCodesDialog(false)}>
              {t.settings.twoFactorBackupCodesSaved}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}